<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Avatar from 'next/avatar/Avatar.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isInputFocused = ref(false);

const getStatusText = computed(() => {
  return props.config.isOnline
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE');
});
</script>

<template>
  <div class="relative flex flex-col w-full px-4 gap-3">
    <!-- Chat Input Area (only in chat mode) -->
    <div
      v-if="!config.isDefaultScreen"
      class="flex items-center h-10 bg-white rounded-md dark:!bg-n-slate-3 shadow-sm border border-n-weak"
      :class="{ 'ring-2 ring-n-brand dark:ring-n-brand': isInputFocused }"
    >
      <ResizableTextArea
        id="chat-input"
        :rows="1"
        :placeholder="
          $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
        "
        class="flex-grow !bg-white border-0 outline-none !outline-0 border-none h-8 text-sm dark:!bg-n-slate-3 pb-0 !pt-1.5 resize-none px-3 !mb-0 focus:outline-none rounded-md"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <div class="flex items-center gap-2 px-2">
        <FluentIcon icon="emoji" class="text-n-slate-11" />
        <FluentIcon class="icon-send text-n-slate-11" icon="send" />
      </div>
    </div>

    <!-- Mock Bottom Tab Bar to match production design -->
    <div class="h-[44px] bg-n-solid-2 dark:bg-n-solid-3 border-t border-n-weak flex items-center justify-around select-none -mx-4 rounded-b-lg">
      <!-- Home Tab -->
      <div 
        class="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
        :class="config.isDefaultScreen ? 'text-n-slate-12 dark:text-white' : 'text-n-slate-11'"
        :style="config.isDefaultScreen ? { color: config.color || '#635BFF' } : {}"
      >
        <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9 22 9 12 15 12 15 22"/>
        </svg>
        <span class="text-[8px] font-bold">Home</span>
      </div>

      <!-- Messages Tab -->
      <div 
        class="flex flex-col items-center justify-center flex-1 h-full gap-0.5"
        :class="!config.isDefaultScreen ? 'text-n-slate-12 dark:text-white' : 'text-n-slate-11'"
        :style="!config.isDefaultScreen ? { color: config.color || '#635BFF' } : {}"
      >
        <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
        </svg>
        <span class="text-[8px] font-bold">Messages</span>
      </div>

      <!-- Help Tab -->
      <div class="flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-n-slate-11">
        <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span class="text-[8px] font-bold">Help</span>
      </div>

      <!-- News Tab -->
      <div class="flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-n-slate-11">
        <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        <span class="text-[8px] font-bold">News</span>
      </div>
    </div>
  </div>
</template>
